import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Button, Grid, InputBase, Divider, Checkbox, Avatar, Link, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, FormControl, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PersonOutlineOutlinedIcon, PersonAddAlt1OutlinedIcon, CloseOutlinedIcon, FileDownloadOutlinedIcon } from 'icons/materialIcons';
import { CSVFileIcon } from 'icons/fileTypeIcons';
import Modal from 'components/common/Modal';
import Papa from 'papaparse';
import { useTeam } from 'team/contexts/teamContext';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import { getSubteam, getSubteamMembers, addMemberToSubteam, removeMemberFromSubteam } from 'team/apis/teamSubteamApi';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import FileImportUtils from 'utils/contacts/FileImportUtils';

const TeamSubteamMembersEditContainer = ({ subteamId }) => {
    const navigate = useNavigate();
    const { currentTeamId } = useTeam();
    const [searchQuery, setSearchQuery] = useState('');
    const [allTeamMembers, setAllTeamMembers] = useState([]);
    const [subteamMembersList, setSubteamMembersList] = useState([]);
    const [selectedMembers, setSelectedMembers] = useState(new Set()); // For "Not part of Subteam"
    const [selectedMembersInSubteam, setSelectedMembersInSubteam] = useState(new Set()); // For "Part of Subteam"
    const [loading, setLoading] = useState(false);
    const [csvModalOpen, setCsvModalOpen] = useState(false);
    const [columnMappingModalOpen, setColumnMappingModalOpen] = useState(false);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);
    const [csvFile, setCsvFile] = useState(null);
    const [csvData, setCsvData] = useState([]);
    const [csvColumns, setCsvColumns] = useState([]);
    const [emailColumnMapping, setEmailColumnMapping] = useState('');
    const [subteamData, setSubteamData] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Fetch team members and subteam members
    useEffect(() => {
        const fetchMembers = async () => {
            if (!currentTeamId || !subteamId) return;

            setLoading(true);
            try {
                // Fetch all team members
                const teamMembersResponse = await getTeamMembers(currentTeamId, { status: 'active' });
                const teamMembers = teamMembersResponse?.members || [];
                console.log('[TeamSubteamMembersEditContainer] All team members:', teamMembers);

                // Log subteamId information for debugging
                console.log('[TeamSubteamMembersEditContainer] Team members with subteamId info:',
                    teamMembers.map(m => ({
                        name: m.user?.fullName || m.fullName,
                        userId: m.userId || m.user?.userId,
                        subteamId: m.subteamId?._id || m.subteamId,
                        subteamIdType: typeof m.subteamId,
                        subteamIdString: m.subteamId?.toString()
                    }))
                );

                setAllTeamMembers(teamMembers);

                // Try to fetch subteam members, but don't fail if it errors
                let subteamMembers = [];
                try {
                    const subteamMembersResponse = await getSubteamMembers(subteamId);
                    if (subteamMembersResponse) {
                        if (Array.isArray(subteamMembersResponse)) {
                            subteamMembers = subteamMembersResponse;
                        } else if (subteamMembersResponse.members) {
                            subteamMembers = subteamMembersResponse.members;
                        } else if (subteamMembersResponse.data && Array.isArray(subteamMembersResponse.data)) {
                            subteamMembers = subteamMembersResponse.data;
                        } else if (subteamMembersResponse.data && subteamMembersResponse.data.members) {
                            subteamMembers = subteamMembersResponse.data.members;
                        }
                    }
                    console.log('[TeamSubteamMembersEditContainer] Subteam members from API:', subteamMembers.length);
                } catch (subteamError) {
                    console.warn('[TeamSubteamMembersEditContainer] getSubteamMembers failed, will use subteamId field from team members:', subteamError);
                    // Fallback: Use getTeamMembers with subteamId filter
                    try {
                        const subteamMembersResponse = await getTeamMembers(currentTeamId, {
                            status: 'active',
                            subteamId: subteamId
                        });
                        subteamMembers = subteamMembersResponse?.members || [];
                        console.log('[TeamSubteamMembersEditContainer] Subteam members from getTeamMembers filter:', subteamMembers.length);
                    } catch (fallbackError) {
                        console.warn('[TeamSubteamMembersEditContainer] Fallback also failed, will filter manually:', fallbackError);
                        // Will be filtered in the component logic using subteamId field
                    }
                }

                setSubteamMembersList(subteamMembers);
                console.log('[TeamSubteamMembersEditContainer] Subteam members list set:', subteamMembers.length);
            } catch (error) {
                console.error('[TeamSubteamMembersEditContainer] Error fetching members:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchMembers();
    }, [currentTeamId, subteamId]);

    // Fetch subteam data for name display
    useEffect(() => {
        const fetchSubteamData = async () => {
            if (!subteamId) return;
            try {
                const response = await getSubteam(subteamId);
                if (response && response.success && response.subteam) {
                    setSubteamData(response.subteam);
                }
            } catch (error) {
                console.error('[TeamSubteamMembersEditContainer] Error fetching subteam data:', error);
            }
        };
        fetchSubteamData();
    }, [subteamId]);

    // Helper function to get member ID for comparison
    const getMemberId = (member) => {
        if (!member) return null;
        return member.userId ||
            member.user?.userId ||
            member.user?._id ||
            member._id ||
            null;
    };

    // Helper function to get member email for comparison
    const getMemberEmail = (member) => {
        if (!member) return null;
        const email = member.user?.email || member.email || '';
        return email.toLowerCase().trim();
    };

    // Helper function to check if two members are the same
    const isSameMember = (teamMember, subteamMember) => {
        // First try to match by ID
        const teamMemberId = getMemberId(teamMember);
        const subteamMemberId = getMemberId(subteamMember);

        if (teamMemberId && subteamMemberId) {
            // Strict ID comparison
            const teamIdStr = String(teamMemberId).trim();
            const subteamIdStr = String(subteamMemberId).trim();

            if (teamIdStr && subteamIdStr && teamIdStr === subteamIdStr) {
                return true;
            }

            // Also check nested user IDs
            if (teamMember.user?.userId && subteamMember.user?.userId) {
                if (String(teamMember.user.userId).trim() === String(subteamMember.user.userId).trim()) {
                    return true;
                }
            }
        }

        // Fallback to email comparison if IDs don't match
        const teamEmail = getMemberEmail(teamMember);
        const subteamEmail = getMemberEmail(subteamMember);

        if (teamEmail && subteamEmail && teamEmail === subteamEmail) {
            return true;
        }

        return false;
    };

    // Helper function to get a member's subteamId (legacy/fallback for logging)
    const getMemberSubteamId = (member) => {
        if (!member) return null;
        // Prefer subteamIds array if present (return first match for logging)
        if (Array.isArray(member.subteamIds) && member.subteamIds.length > 0) {
            const first = member.subteamIds[0];
            if (typeof first === 'string') return first.toString();
            if (first?._id) return first._id.toString();
            if (first?.toString && typeof first.toString === 'function') return first.toString();
        }
        // Fallback to legacy subteamId
        const subteamIdValue = member.subteamId?._id ||
            member.subteamId ||
            member.subteamId?.toString() ||
            null;
        return subteamIdValue ? subteamIdValue.toString() : null;
    };

    // Helper function to check if a member belongs to a subteam (handles subteamIds array)
    const memberHasSubteamId = (member, targetSubteamId) => {
        if (!member || !targetSubteamId) return false;
        const targetId = targetSubteamId.toString();

        if (Array.isArray(member.subteamIds)) {
            return member.subteamIds.some((subteam) => {
                if (!subteam) return false;
                if (typeof subteam === 'string') return subteam === targetId;
                if (subteam._id) return subteam._id.toString() === targetId;
                if (subteam.toString && typeof subteam.toString === 'function') {
                    return subteam.toString() === targetId;
                }
                return false;
            });
        }

        // Legacy fallback
        const legacyId = member.subteamId?._id || member.subteamId || member.subteamId?.toString();
        return legacyId ? legacyId.toString() === targetId : false;
    };

    // Filter members - check if team member is in subteam
    // First try to use subteamMembersList if available, otherwise check subteamId field directly
    // Use useMemo to ensure proper re-rendering when searchQuery changes
    const membersInSubteam = useMemo(() => {
        // Helper function to check if member matches search query (defined inside useMemo to use current searchQuery)
        const matchesSearchQuery = (member) => {
            if (!searchQuery || searchQuery.trim().length === 0) return true;
            const query = searchQuery.toLowerCase().trim();
            const fullName = (member.user?.fullName || member.fullName || '').toLowerCase();
            const email = (member.user?.email || member.email || '').toLowerCase();

            const matches = fullName.includes(query) || email.includes(query);

            // Debug logging for single character searches
            if (query.length === 1) {
                console.log('[TeamSubteamMembersEditContainer] Single char search (InSubteam):', {
                    query,
                    memberName: member.user?.fullName || member.fullName,
                    fullName,
                    email,
                    matches,
                    fullNameIncludes: fullName.includes(query),
                    emailIncludes: email.includes(query)
                });
            }

            return matches;
        };

        const filtered = allTeamMembers.filter(member => {
            // If we have a subteamMembersList, use it for matching
            if (subteamMembersList && subteamMembersList.length > 0) {
                const isInSubteam = subteamMembersList.some(subteamMember => {
                    return isSameMember(member, subteamMember);
                });
                if (isInSubteam) return matchesSearchQuery(member);
            }

            // Fallback: Check membership using subteamIds array or legacy field
            if (memberHasSubteamId(member, subteamId)) {
                return matchesSearchQuery(member);
            }

            return false;
        });
        // Return a new array reference to ensure React detects the change
        return [...filtered];
    }, [allTeamMembers, subteamMembersList, subteamId, searchQuery]);

    // Members not in subteam - filter by excluding members in subteam
    const membersNotInSubteam = useMemo(() => {
        // Helper function to check if member matches search query (defined inside useMemo to use current searchQuery)
        const matchesSearchQuery = (member) => {
            if (!searchQuery || searchQuery.trim().length === 0) return true;
            const query = searchQuery.toLowerCase().trim();
            const fullName = (member.user?.fullName || member.fullName || '').toLowerCase();
            const email = (member.user?.email || member.email || '').toLowerCase();

            const matches = fullName.includes(query) || email.includes(query);

            // Debug logging for single character searches
            if (query.length === 1) {
                console.log('[TeamSubteamMembersEditContainer] Single char search (NotInSubteam):', {
                    query,
                    memberName: member.user?.fullName || member.fullName,
                    fullName,
                    email,
                    matches,
                    fullNameIncludes: fullName.includes(query),
                    emailIncludes: email.includes(query)
                });
            }

            return matches;
        };

        const filtered = allTeamMembers.filter(member => {
            // If we have a subteamMembersList, use it for matching
            if (subteamMembersList && subteamMembersList.length > 0) {
                const isInSubteam = subteamMembersList.some(subteamMember => {
                    return isSameMember(member, subteamMember);
                });
                // If found in subteamMembersList, exclude it
                if (isInSubteam) return false;
            }

            // Fallback: Check membership using subteamIds array or legacy field
            if (memberHasSubteamId(member, subteamId)) {
                return false; // member is in subteam
            }

            // If member has no subteamId or it doesn't match, include it
            return true;
        }).filter(member => matchesSearchQuery(member));
        // Return a new array reference to ensure React detects the change
        return [...filtered];
    }, [allTeamMembers, subteamMembersList, subteamId, searchQuery]);

    const handleSearchChange = (event) => {
        const newValue = event.target.value;
        console.log('[TeamSubteamMembersEditContainer] Search query changed:', {
            oldValue: searchQuery,
            newValue,
            length: newValue.length
        });
        // Force update by setting search query
        setSearchQuery(newValue);
        // Force a re-render by updating a dummy state if needed
        // The useMemo should handle this, but this ensures it happens
    };

    const handleAddMembersViaCSV = () => {
        setCsvModalOpen(true);
    };

    const handleCsvModalClose = () => {
        setCsvModalOpen(false);
        setDragActive(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        // Reset CSV data
        setCsvFile(null);
        setCsvData([]);
        setCsvColumns([]);
        setEmailColumnMapping('');
    };

    const handleColumnMappingContinue = () => {
        if (!emailColumnMapping) {
            setSnackbar({
                open: true,
                message: 'Please select an email column to continue.',
                severity: 'error'
            });
            return;
        }
        setColumnMappingModalOpen(false);
        setConfirmationModalOpen(true);
    };

    const handleColumnMappingCancel = () => {
        setColumnMappingModalOpen(false);
        handleCsvModalClose();
    };

    const handleConfirmationCancel = () => {
        setConfirmationModalOpen(false);
        handleCsvModalClose();
    };

    // Email validation helper
    const isValidEmail = (email) => {
        if (!email || typeof email !== 'string') return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
    };

    const handleImportMembers = async () => {
        if (!emailColumnMapping || !csvData || csvData.length === 0) {
            setSnackbar({
                open: true,
                message: 'No data to import.',
                severity: 'error'
            });
            return;
        }

        // ✅ IMPROVEMENT 1: Refresh team members list before import to get latest data
        try {
            const teamMembersResponse = await getTeamMembers(currentTeamId, { status: 'active' });
            const freshTeamMembers = teamMembersResponse?.members || [];
            setAllTeamMembers(freshTeamMembers);
        } catch (error) {
            console.warn('Error refreshing team members before import:', error);
            // Continue with existing data
        }

        // Extract and validate emails from CSV data
        const rawEmails = csvData
            .map(row => row[emailColumnMapping])
            .filter(email => email && email.trim());

        // ✅ IMPROVEMENT 2: Validate email format before processing
        const validEmails = [];
        const invalidEmails = [];

        rawEmails.forEach(email => {
            const trimmedEmail = email.trim().toLowerCase();
            if (isValidEmail(trimmedEmail)) {
                validEmails.push(trimmedEmail);
            } else {
                invalidEmails.push(email.trim());
            }
        });

        // Remove duplicates
        const uniqueEmails = [...new Set(validEmails)];

        if (uniqueEmails.length === 0) {
            let errorMessage = 'No valid emails found in the selected column.';
            if (invalidEmails.length > 0) {
                errorMessage += ` ${invalidEmails.length} invalid email${invalidEmails.length !== 1 ? 's' : ''} found.`;
            }
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
            return;
        }

        // Close confirmation modal
        setConfirmationModalOpen(false);

        // Show progress snackbar
        setSnackbar({
            open: true,
            message: `Processing ${uniqueEmails.length} email${uniqueEmails.length !== 1 ? 's' : ''}... You'll be alerted when complete`,
            severity: 'info'
        });

        setIsImporting(true);

        try {
            // Get current subteam members to check for duplicates
            let currentSubteamMembers = [];
            try {
                const subteamMembersResponse = await getSubteamMembers(subteamId);
                if (subteamMembersResponse) {
                    if (Array.isArray(subteamMembersResponse)) {
                        currentSubteamMembers = subteamMembersResponse;
                    } else if (subteamMembersResponse.members) {
                        currentSubteamMembers = subteamMembersResponse.members;
                    } else if (subteamMembersResponse.data && Array.isArray(subteamMembersResponse.data)) {
                        currentSubteamMembers = subteamMembersResponse.data;
                    } else if (subteamMembersResponse.data && subteamMembersResponse.data.members) {
                        currentSubteamMembers = subteamMembersResponse.data.members;
                    }
                }
            } catch (error) {
                console.warn('Could not fetch current subteam members, will check duplicates during import:', error);
            }

            const currentSubteamMemberEmails = new Set(
                currentSubteamMembers.map(m => getMemberEmail(m)).filter(Boolean)
            );

            // ✅ IMPROVEMENT 3: Process in batches for better performance
            const batchSize = 10; // Process 10 members at a time
            const batches = [];
            for (let i = 0; i < uniqueEmails.length; i += batchSize) {
                batches.push(uniqueEmails.slice(i, i + batchSize));
            }

            let successCount = 0;
            let errorCount = 0;
            const failedEmails = [];
            const duplicateEmails = [];
            const notFoundEmails = [];

            // Process each batch
            for (const batch of batches) {
                // Process batch in parallel
                const batchResults = await Promise.all(
                    batch.map(async (email) => {
                        try {
                            // ✅ IMPROVEMENT 4: Check for duplicates before API call
                            if (currentSubteamMemberEmails.has(email)) {
                                return { email, status: 'duplicate', reason: 'Already in subteam' };
                            }

                            // Find member by email in existing team members
                            // ✅ Only existing Touch users who are team members can be added
                            const member = allTeamMembers.find(m => {
                                const memberEmail = getMemberEmail(m);
                                return memberEmail === email;
                            });

                            if (!member) {
                                return { email, status: 'not_found', reason: 'Not found as team member. Only existing Touch users who are team members can be added.' };
                            }

                            const memberId = getMemberId(member);
                            if (!memberId) {
                                return { email, status: 'error', reason: 'Invalid member data' };
                            }

                            // Add member to subteam (backend will verify user exists in Touch)
                            await addMemberToSubteam(subteamId, memberId.toString());

                            // Update duplicate check set for next iterations
                            currentSubteamMemberEmails.add(email);

                            return { email, status: 'success' };
                        } catch (error) {
                            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
                            return { email, status: 'error', reason: errorMessage };
                        }
                    })
                );

                // Process batch results
                batchResults.forEach(result => {
                    if (result.status === 'success') {
                        successCount++;
                    } else {
                        errorCount++;
                        if (result.status === 'duplicate') {
                            duplicateEmails.push(result.email);
                            failedEmails.push({ email: result.email, reason: result.reason });
                        } else if (result.status === 'not_found') {
                            notFoundEmails.push(result.email);
                            failedEmails.push({ email: result.email, reason: result.reason });
                        } else {
                            failedEmails.push({ email: result.email, reason: result.reason });
                        }
                    }
                });
            }

            // Refresh members list after import
            const teamMembersResponse = await getTeamMembers(currentTeamId, { status: 'active' });
            setAllTeamMembers(teamMembersResponse?.members || []);

            // ✅ IMPROVEMENT 5: Show detailed results with specific failure reasons
            let message = '';
            const parts = [];

            if (successCount > 0) {
                parts.push(`${successCount} member${successCount !== 1 ? 's' : ''} added`);
            }

            if (duplicateEmails.length > 0) {
                parts.push(`${duplicateEmails.length} already in subteam`);
            }

            if (notFoundEmails.length > 0) {
                parts.push(`${notFoundEmails.length} not found as team member${notFoundEmails.length !== 1 ? 's' : ''}`);
            }

            if (errorCount > duplicateEmails.length + notFoundEmails.length) {
                const otherErrors = errorCount - duplicateEmails.length - notFoundEmails.length;
                parts.push(`${otherErrors} failed`);
            }

            if (invalidEmails.length > 0) {
                parts.push(`${invalidEmails.length} invalid email${invalidEmails.length !== 1 ? 's' : ''}`);
            }

            if (parts.length === 0) {
                message = 'No members were added.';
            } else {
                message = parts.join(', ') + '.';
            }

            // Determine severity
            let severity = 'success';
            if (errorCount > 0 && successCount === 0) {
                severity = 'error';
            } else if (errorCount > 0) {
                severity = 'warning';
            }

            setSnackbar({
                open: true,
                message: message,
                severity: severity
            });

            // Log detailed results for debugging
            console.log('[CSV Import] Results:', {
                total: uniqueEmails.length,
                success: successCount,
                errors: errorCount,
                duplicates: duplicateEmails.length,
                notFound: notFoundEmails.length,
                invalid: invalidEmails.length,
                failedEmails,
                invalidEmails
            });

            // Close all modals and reset
            handleCsvModalClose();
        } catch (error) {
            console.error('Error importing members:', error);
            setSnackbar({
                open: true,
                message: 'Error importing members. Only existing Touch users who are team members can be added via CSV.',
                severity: 'error'
            });
        } finally {
            setIsImporting(false);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const downloadTemplate = () => {
        const csvContent = "email\nexample@company.com\n";
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'team_members_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!dragActive) setDragActive(true);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            handleFileSelect(files[0]);
        }
    };

    const handleFileInput = (e) => {
        const files = e.target.files;
        if (files && files[0]) {
            handleFileSelect(files[0]);
        }
    };

    const handleFileSelect = async (file) => {
        // Validate file type
        const validTypes = ['.csv', '.xlsx', '.xls', '.ods', '.txt'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!validTypes.includes(fileExtension)) {
            setSnackbar({
                open: true,
                message: 'Invalid file type. Please upload a CSV or Excel file.',
                severity: 'error'
            });
            return;
        }

        // Validate file size (50MB max)
        const maxSize = 50 * 1024 * 1024; // 50MB in bytes
        if (file.size > maxSize) {
            setSnackbar({
                open: true,
                message: 'File size exceeds 50MB limit.',
                severity: 'error'
            });
            return;
        }

        setCsvFile(file);

        // Parse file using FileImportUtils (handles both CSV and Excel files)
        try {
            // Use FileImportUtils.importFile() to handle both CSV and Excel files (same as leads import)
            const { data: importedData, columns } = await FileImportUtils.importFile(file);

            // Filter out empty rows to ensure clean data
            const validRows = importedData.filter(row => {
                return Object.values(row).some(val =>
                    val !== null && val !== undefined && val !== '');
            });

            if (!validRows || validRows.length === 0) {
                setSnackbar({
                    open: true,
                    message: 'File is empty or has no valid data.',
                    severity: 'error'
                });
                return;
            }

            // Set columns and data
            setCsvColumns(columns);
            setCsvData(validRows);

            // Reset email column mapping to empty (Unmapped)
            setEmailColumnMapping('');

            // Close upload modal and open column mapping modal
            setCsvModalOpen(false);
            setColumnMappingModalOpen(true);
        } catch (error) {
            console.error('Error processing file:', error);
            setSnackbar({
                open: true,
                message: error.message || 'Error processing file. Please try again.',
                severity: 'error'
            });
        }
    };

    const handleSelectAllNotInSubteam = () => {
        if (selectedMembers.size === membersNotInSubteam.length && membersNotInSubteam.length > 0) {
            // Unselect all
            setSelectedMembers(new Set());
        } else {
            // Select all
            const allIds = new Set(
                membersNotInSubteam
                    .map(m => {
                        const id = getMemberId(m);
                        return id ? id.toString() : null;
                    })
                    .filter(Boolean)
            );
            setSelectedMembers(allIds);
        }
    };

    const handleSelectAllInSubteam = () => {
        if (selectedMembersInSubteam.size === membersInSubteam.length && membersInSubteam.length > 0) {
            // Unselect all
            setSelectedMembersInSubteam(new Set());
        } else {
            // Select all
            const allIds = new Set(
                membersInSubteam
                    .map(m => {
                        const id = getMemberId(m);
                        return id ? id.toString() : null;
                    })
                    .filter(Boolean)
            );
            setSelectedMembersInSubteam(allIds);
        }
    };

    const handleMemberToggle = (memberId, isInSubteam = false) => {
        if (!memberId) return;
        const memberIdStr = memberId.toString();

        if (isInSubteam) {
            const newSelected = new Set(selectedMembersInSubteam);
            if (newSelected.has(memberIdStr)) {
                newSelected.delete(memberIdStr);
            } else {
                newSelected.add(memberIdStr);
            }
            setSelectedMembersInSubteam(newSelected);
        } else {
            const newSelected = new Set(selectedMembers);
            if (newSelected.has(memberIdStr)) {
                newSelected.delete(memberIdStr);
            } else {
                newSelected.add(memberIdStr);
            }
            setSelectedMembers(newSelected);
        }
    };

    const handleAddMembers = async () => {
        if (selectedMembers.size === 0) return;

        setLoading(true);
        try {
            // Get the actual member objects for the selected IDs
            const membersToAdd = membersNotInSubteam.filter(member => {
                const memberId = getMemberId(member);
                return memberId && selectedMembers.has(memberId.toString());
            });

            if (membersToAdd.length === 0) {
                console.warn('[TeamSubteamMembersEditContainer] No valid members to add');
                setLoading(false);
                return;
            }

            console.log('[TeamSubteamMembersEditContainer] Adding members:', {
                selectedIds: Array.from(selectedMembers),
                membersToAdd: membersToAdd.map(m => ({
                    id: getMemberId(m),
                    name: m.user?.fullName || m.fullName,
                    email: getMemberEmail(m)
                }))
            });

            // Add each member to the subteam
            const addPromises = membersToAdd.map(async (member) => {
                const memberId = getMemberId(member);
                if (!memberId) {
                    console.warn('[TeamSubteamMembersEditContainer] Skipping member with no ID:', member);
                    return { success: false, memberId: null, error: 'No member ID' };
                }

                try {
                    const result = await addMemberToSubteam(subteamId, memberId.toString());

                    // Check if the result indicates success
                    const isSuccess = result && (result.success === true || result.message || !result.error);

                    console.log('[TeamSubteamMembersEditContainer] Member add response:', {
                        memberId,
                        result,
                        isSuccess
                    });

                    if (isSuccess) {
                        return { success: true, memberId };
                    } else {
                        console.error('[TeamSubteamMembersEditContainer] Member add failed:', {
                            memberId,
                            result
                        });
                        return { success: false, memberId, error: result?.error || 'Unknown error' };
                    }
                } catch (error) {
                    console.error('[TeamSubteamMembersEditContainer] Error adding member:', {
                        memberId,
                        error: error.message || error
                    });
                    return { success: false, memberId, error: error.message || 'API error' };
                }
            });

            const results = await Promise.all(addPromises);
            const successful = results.filter(r => r && r.success);
            const failed = results.filter(r => r && !r.success);

            console.log('[TeamSubteamMembersEditContainer] Add results:', {
                total: results.length,
                successful: successful.length,
                failed: failed.length,
                successfulIds: successful.map(r => r.memberId),
                failedDetails: failed
            });

            if (failed.length > 0) {
                console.warn('[TeamSubteamMembersEditContainer] Some members failed to add:', failed);
            }

            // Wait a bit to ensure backend has processed the changes
            await new Promise(resolve => setTimeout(resolve, 500));

            // Refresh members list to get updated data
            console.log('[TeamSubteamMembersEditContainer] Refreshing members list...');

            // Refresh all team members first
            const teamMembersResponse = await getTeamMembers(currentTeamId, { status: 'active' });
            const teamMembers = teamMembersResponse?.members || [];
            console.log('[TeamSubteamMembersEditContainer] Team members fetched:', teamMembers.length);
            setAllTeamMembers(teamMembers);

            // Try to get subteam members using getSubteamMembers, but fallback to getTeamMembers with subteamId filter
            let subteamMembers = [];
            try {
                const subteamMembersResponse = await getSubteamMembers(subteamId);
                console.log('[TeamSubteamMembersEditContainer] Subteam members response:', subteamMembersResponse);

                // Handle different response structures
                if (subteamMembersResponse) {
                    if (Array.isArray(subteamMembersResponse)) {
                        subteamMembers = subteamMembersResponse;
                    } else if (subteamMembersResponse.members) {
                        subteamMembers = subteamMembersResponse.members;
                    } else if (subteamMembersResponse.data && Array.isArray(subteamMembersResponse.data)) {
                        subteamMembers = subteamMembersResponse.data;
                    } else if (subteamMembersResponse.data && subteamMembersResponse.data.members) {
                        subteamMembers = subteamMembersResponse.data.members;
                    }
                }
            } catch (subteamError) {
                console.warn('[TeamSubteamMembersEditContainer] getSubteamMembers failed, using getTeamMembers with subteamId filter:', subteamError);
                // Fallback: Use getTeamMembers with subteamId filter
                try {
                    const subteamMembersResponse = await getTeamMembers(currentTeamId, {
                        status: 'active',
                        subteamId: subteamId
                    });
                    subteamMembers = subteamMembersResponse?.members || [];
                    console.log('[TeamSubteamMembersEditContainer] Subteam members from getTeamMembers:', subteamMembers.length);
                } catch (fallbackError) {
                    console.error('[TeamSubteamMembersEditContainer] Fallback also failed:', fallbackError);
                    // Last resort: Filter team members by subteamId manually
                    subteamMembers = teamMembers.filter(member => {
                        const memberSubteamId = member.subteamId?._id || member.subteamId || member.subteamId?.toString();
                        return memberSubteamId && memberSubteamId.toString() === subteamId.toString();
                    });
                    console.log('[TeamSubteamMembersEditContainer] Subteam members from manual filter:', subteamMembers.length);
                }
            }

            console.log('[TeamSubteamMembersEditContainer] Subteam members extracted:', subteamMembers.length);
            setSubteamMembersList(subteamMembers);

            // Clear selection after add attempt
            setSelectedMembers(new Set());

            console.log('[TeamSubteamMembersEditContainer] Members refreshed:', {
                totalTeamMembers: teamMembers.length,
                subteamMembers: subteamMembers.length,
                membersInSubteamBefore: membersInSubteam.length,
                membersNotInSubteamBefore: membersNotInSubteam.length
            });
        } catch (error) {
            console.error('[TeamSubteamMembersEditContainer] Error adding members to subteam:', error);
            // Still refresh to show current state
            try {
                const teamMembersResponse = await getTeamMembers(currentTeamId, { status: 'active' });
                const teamMembers = teamMembersResponse?.members || [];
                setAllTeamMembers(teamMembers);

                // Try getSubteamMembers first, then fallback to getTeamMembers with subteamId
                let subteamMembers = [];
                try {
                    const subteamMembersResponse = await getSubteamMembers(subteamId);
                    if (subteamMembersResponse) {
                        if (Array.isArray(subteamMembersResponse)) {
                            subteamMembers = subteamMembersResponse;
                        } else if (subteamMembersResponse.members) {
                            subteamMembers = subteamMembersResponse.members;
                        } else if (subteamMembersResponse.data && Array.isArray(subteamMembersResponse.data)) {
                            subteamMembers = subteamMembersResponse.data;
                        } else if (subteamMembersResponse.data && subteamMembersResponse.data.members) {
                            subteamMembers = subteamMembersResponse.data.members;
                        }
                    }
                } catch (subteamError) {
                    // Fallback: Use getTeamMembers with subteamId filter
                    try {
                        const subteamMembersResponse = await getTeamMembers(currentTeamId, {
                            status: 'active',
                            subteamId: subteamId
                        });
                        subteamMembers = subteamMembersResponse?.members || [];
                    } catch (fallbackError) {
                        // Last resort: Filter manually
                        subteamMembers = teamMembers.filter(member => {
                            const memberSubteamId = member.subteamId?._id || member.subteamId || member.subteamId?.toString();
                            return memberSubteamId && memberSubteamId.toString() === subteamId.toString();
                        });
                    }
                }
                setSubteamMembersList(subteamMembers);
            } catch (refreshError) {
                console.error('[TeamSubteamMembersEditContainer] Error refreshing members:', refreshError);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMembers = async () => {
        if (selectedMembersInSubteam.size === 0) return;

        setLoading(true);
        try {
            // Get the actual member objects for the selected IDs
            const membersToRemove = membersInSubteam.filter(member => {
                const memberId = getMemberId(member);
                return memberId && selectedMembersInSubteam.has(memberId.toString());
            });

            if (membersToRemove.length === 0) {
                console.warn('[TeamSubteamMembersEditContainer] No valid members to remove');
                setLoading(false);
                return;
            }

            console.log('[TeamSubteamMembersEditContainer] Removing members:', {
                selectedIds: Array.from(selectedMembersInSubteam),
                membersToRemove: membersToRemove.map(m => ({
                    id: getMemberId(m),
                    name: m.user?.fullName || m.fullName,
                    email: getMemberEmail(m)
                }))
            });

            // Remove each member from the subteam
            const removePromises = membersToRemove.map(async (member) => {
                const memberId = getMemberId(member);
                if (!memberId) {
                    console.warn('[TeamSubteamMembersEditContainer] Skipping member with no ID:', member);
                    return { success: false, memberId: null, error: 'No member ID' };
                }

                try {
                    const result = await removeMemberFromSubteam(subteamId, memberId.toString());

                    // Check if the result indicates success
                    const isSuccess = result && (result.success === true || result.message || !result.error);

                    console.log('[TeamSubteamMembersEditContainer] Member remove response:', {
                        memberId,
                        result,
                        isSuccess
                    });

                    if (isSuccess) {
                        return { success: true, memberId };
                    } else {
                        console.error('[TeamSubteamMembersEditContainer] Member remove failed:', {
                            memberId,
                            result
                        });
                        return { success: false, memberId, error: result?.error || 'Unknown error' };
                    }
                } catch (error) {
                    console.error('[TeamSubteamMembersEditContainer] Error removing member:', {
                        memberId,
                        error: error.message || error
                    });
                    return { success: false, memberId, error: error.message || 'API error' };
                }
            });

            const results = await Promise.all(removePromises);
            const successful = results.filter(r => r && r.success);
            const failed = results.filter(r => r && !r.success);

            console.log('[TeamSubteamMembersEditContainer] Remove results:', {
                total: results.length,
                successful: successful.length,
                failed: failed.length,
                successfulIds: successful.map(r => r.memberId),
                failedDetails: failed
            });

            if (failed.length > 0) {
                console.warn('[TeamSubteamMembersEditContainer] Some members failed to remove:', failed);
            }

            // Wait a bit to ensure backend has processed the changes
            await new Promise(resolve => setTimeout(resolve, 500));

            // Refresh members list to get updated data
            console.log('[TeamSubteamMembersEditContainer] Refreshing members list after removal...');

            // Refresh all team members first
            const teamMembersResponse = await getTeamMembers(currentTeamId, { status: 'active' });
            const teamMembers = teamMembersResponse?.members || [];
            console.log('[TeamSubteamMembersEditContainer] Team members fetched:', teamMembers.length);
            setAllTeamMembers(teamMembers);

            // Try to get subteam members using getSubteamMembers, but fallback to getTeamMembers with subteamId filter
            let subteamMembers = [];
            try {
                const subteamMembersResponse = await getSubteamMembers(subteamId);
                console.log('[TeamSubteamMembersEditContainer] Subteam members response:', subteamMembersResponse);

                // Handle different response structures
                if (subteamMembersResponse) {
                    if (Array.isArray(subteamMembersResponse)) {
                        subteamMembers = subteamMembersResponse;
                    } else if (subteamMembersResponse.members) {
                        subteamMembers = subteamMembersResponse.members;
                    } else if (subteamMembersResponse.data && Array.isArray(subteamMembersResponse.data)) {
                        subteamMembers = subteamMembersResponse.data;
                    } else if (subteamMembersResponse.data && subteamMembersResponse.data.members) {
                        subteamMembers = subteamMembersResponse.data.members;
                    }
                }
            } catch (subteamError) {
                console.warn('[TeamSubteamMembersEditContainer] getSubteamMembers failed, using getTeamMembers with subteamId filter:', subteamError);
                // Fallback: Use getTeamMembers with subteamId filter
                try {
                    const subteamMembersResponse = await getTeamMembers(currentTeamId, {
                        status: 'active',
                        subteamId: subteamId
                    });
                    subteamMembers = subteamMembersResponse?.members || [];
                    console.log('[TeamSubteamMembersEditContainer] Subteam members from getTeamMembers:', subteamMembers.length);
                } catch (fallbackError) {
                    console.error('[TeamSubteamMembersEditContainer] Fallback also failed:', fallbackError);
                    // Last resort: Filter team members by subteamId manually
                    subteamMembers = teamMembers.filter(member => {
                        const memberSubteamId = member.subteamId?._id || member.subteamId || member.subteamId?.toString();
                        return memberSubteamId && memberSubteamId.toString() === subteamId.toString();
                    });
                    console.log('[TeamSubteamMembersEditContainer] Subteam members from manual filter:', subteamMembers.length);
                }
            }

            console.log('[TeamSubteamMembersEditContainer] Subteam members extracted:', subteamMembers.length);
            setSubteamMembersList(subteamMembers);

            // Clear selection after remove attempt
            setSelectedMembersInSubteam(new Set());

            console.log('[TeamSubteamMembersEditContainer] Members refreshed after removal:', {
                totalTeamMembers: teamMembers.length,
                subteamMembers: subteamMembers.length
            });
        } catch (error) {
            console.error('[TeamSubteamMembersEditContainer] Error removing members from subteam:', error);
            // Still refresh to show current state
            try {
                const teamMembersResponse = await getTeamMembers(currentTeamId, { status: 'active' });
                const teamMembers = teamMembersResponse?.members || [];
                setAllTeamMembers(teamMembers);

                // Try getSubteamMembers first, then fallback to getTeamMembers with subteamId
                let subteamMembers = [];
                try {
                    const subteamMembersResponse = await getSubteamMembers(subteamId);
                    if (subteamMembersResponse) {
                        if (Array.isArray(subteamMembersResponse)) {
                            subteamMembers = subteamMembersResponse;
                        } else if (subteamMembersResponse.members) {
                            subteamMembers = subteamMembersResponse.members;
                        } else if (subteamMembersResponse.data && Array.isArray(subteamMembersResponse.data)) {
                            subteamMembers = subteamMembersResponse.data;
                        } else if (subteamMembersResponse.data && subteamMembersResponse.data.members) {
                            subteamMembers = subteamMembersResponse.data.members;
                        }
                    }
                } catch (subteamError) {
                    // Fallback: Use getTeamMembers with subteamId filter
                    try {
                        const subteamMembersResponse = await getTeamMembers(currentTeamId, {
                            status: 'active',
                            subteamId: subteamId
                        });
                        subteamMembers = subteamMembersResponse?.members || [];
                    } catch (fallbackError) {
                        // Last resort: Filter manually
                        subteamMembers = teamMembers.filter(member => {
                            const memberSubteamId = member.subteamId?._id || member.subteamId || member.subteamId?.toString();
                            return memberSubteamId && memberSubteamId.toString() === subteamId.toString();
                        });
                    }
                }
                setSubteamMembersList(subteamMembers);
            } catch (refreshError) {
                console.error('[TeamSubteamMembersEditContainer] Error refreshing members:', refreshError);
            }
        } finally {
            setLoading(false);
        }
    };

    const isAllSelectedNotInSubteam = membersNotInSubteam.length > 0 && selectedMembers.size === membersNotInSubteam.length;
    const isAllSelectedInSubteam = membersInSubteam.length > 0 && selectedMembersInSubteam.size === membersInSubteam.length;

    // Debug logging for final counts
    console.log('[TeamSubteamMembersEditContainer] Final counts:', {
        totalTeamMembers: allTeamMembers.length,
        membersInSubteam: membersInSubteam.length,
        membersNotInSubteam: membersNotInSubteam.length,
        subteamMembersList: subteamMembersList.length,
        currentSubteamId: subteamId,
        searchQuery: searchQuery,
        searchQueryLength: searchQuery.length,
        membersNotInSubteamArray: membersNotInSubteam,
        membersInSubteamArray: membersInSubteam,
        membersInSubteamDetails: membersInSubteam.map(m => ({
            name: m.user?.fullName || m.fullName,
            email: getMemberEmail(m),
            userId: getMemberId(m),
            subteamId: getMemberSubteamId(m)
        })),
        membersNotInSubteamDetails: membersNotInSubteam.map(m => ({
            name: m.user?.fullName || m.fullName,
            email: getMemberEmail(m),
            userId: getMemberId(m),
            subteamId: getMemberSubteamId(m)
        })),
        allTeamMembersSubteamIds: allTeamMembers.map(m => ({
            name: m.user?.fullName || m.fullName,
            subteamId: getMemberSubteamId(m),
            subteamIdRaw: m.subteamId
        }))
    });

    return (
        <Box sx={{ padding: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="h6" sx={{ marginBottom: 3 }}>Edit Members</Typography>

            {/* Scrollable Content Area */}
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {/* Search and Add Members via CSV Row */}
                <Grid container spacing={2} alignItems="center" sx={{ marginBottom: 3 }}>
                    {/* Search Field - Left Side */}
                    <Grid item xs={12} sm={8} md={9}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: '#f7f7f7',
                                borderRadius: '1.5rem',
                                padding: '0.25rem 1rem',
                                width: '100%'
                            }}
                        >
                            <PersonOutlineOutlinedIcon sx={{ color: '#828282', marginRight: 1 }} />
                            <InputBase
                                placeholder="Search..."
                                sx={{
                                    width: '100%',
                                    '& input::placeholder': {
                                        color: '#9d9d9d',
                                        opacity: 1,
                                    },
                                }}
                                value={searchQuery}
                                onChange={handleSearchChange}
                            />
                        </Box>
                    </Grid>

                    {/* Add Members via CSV Button - Right Side */}
                    <Grid item xs={12} sm={4} md={3}>
                        <Button
                            variant="outlined"
                            onClick={handleAddMembersViaCSV}
                            startIcon={<PersonAddAlt1OutlinedIcon sx={{ color: '#a7a7a7' }} />}
                            sx={{
                                backgroundColor: '#fff',
                                color: '#4f4f4f',
                                border: '1px solid #e0e0e0',
                                borderRadius: '1.5rem',
                                textTransform: 'none',
                                padding: '10px 24px',
                                width: '100%',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                    border: '1px solid #e0e0e0',
                                },
                            }}
                        >
                            Add Members via CSV
                        </Button>
                    </Grid>
                </Grid>

                {/* Table-like Layout: Part of Subteam (Left) | Not part of Subteam (Right) */}
                <Grid container spacing={12} sx={{ marginBottom: 3 }}>
                    {/* Left Column: Part of Subteam */}
                    <Grid item xs={12} md={6}>
                        {/* Header with Select All */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: '#4f4f4f'
                                }}
                            >
                                Part of Subteam ({membersInSubteam.length})
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                    checked={isAllSelectedInSubteam}
                                    onChange={handleSelectAllInSubteam}
                                    sx={{
                                        color: '#e0e0e0',
                                        padding: '0',
                                        marginRight: '0.5rem',
                                        '&.Mui-checked': {
                                            color: '#000',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            borderRadius: '0.5rem',
                                        },
                                    }}
                                />
                                <Typography
                                    onClick={handleSelectAllInSubteam}
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        color: '#4f4f4f',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {isAllSelectedInSubteam ? 'Unselect All' : 'Select All'}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider sx={{ borderColor: '#e0e0e0', marginBottom: 2 }} />

                        {/* Member list */}
                        <Box key={`in-subteam-${searchQuery}`}>
                            {membersInSubteam.length === 0 && !loading && (
                                <Typography sx={{ color: '#9d9d9d', fontSize: '0.875rem', padding: '20px 0' }}>
                                    No members
                                </Typography>
                            )}
                            {membersInSubteam.map((member, index) => {
                                const memberId = getMemberId(member);
                                const isSelected = memberId && selectedMembersInSubteam.has(memberId?.toString());
                                const fullName = member.user?.fullName || member.fullName || 'Unknown';
                                const email = member.user?.email || member.email || '';
                                const profilePicture = member.user?.profilePictureUrl || member.profilePictureUrl;

                                return (
                                    <Box
                                        key={`${memberId || member._id}-${searchQuery}-${index}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '12px 0',
                                            borderBottom: '1px solid #f0f0f0'
                                        }}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => handleMemberToggle(memberId, true)}
                                            disabled={!memberId}
                                            sx={{
                                                color: '#e0e0e0',
                                                padding: '0',
                                                marginRight: '12px',
                                                '&.Mui-checked': {
                                                    color: '#000',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    borderRadius: '0.5rem',
                                                },
                                            }}
                                        />
                                        <Avatar
                                            src={profilePicture}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                marginRight: '12px',
                                                backgroundColor: '#e0e0e0'
                                            }}
                                        >
                                            {fullName.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    color: '#4f4f4f'
                                                }}
                                            >
                                                {fullName}
                                            </Typography>
                                            {email && (
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        color: '#9d9d9d'
                                                    }}
                                                >
                                                    {email}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Grid>

                    {/* Right Column: Not part of Subteam */}
                    <Grid item xs={12} md={6}>
                        {/* Header with Select All */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: '#4f4f4f'
                                }}
                            >
                                Not part of Subteam ({membersNotInSubteam.length})
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox
                                    checked={isAllSelectedNotInSubteam}
                                    onChange={handleSelectAllNotInSubteam}
                                    sx={{
                                        color: '#e0e0e0',
                                        padding: '0',
                                        marginRight: '0.5rem',
                                        '&.Mui-checked': {
                                            color: '#000',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            borderRadius: '0.5rem',
                                        },
                                    }}
                                />
                                <Typography
                                    onClick={handleSelectAllNotInSubteam}
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        color: '#4f4f4f',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {isAllSelectedNotInSubteam ? 'Unselect All' : 'Select All'}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider sx={{ borderColor: '#e0e0e0', marginBottom: 2 }} />

                        {/* Member list */}
                        <Box key={`not-in-subteam-${searchQuery}`}>
                            {(() => {
                                console.log('[TeamSubteamMembersEditContainer] Rendering membersNotInSubteam:', {
                                    count: membersNotInSubteam.length,
                                    searchQuery,
                                    members: membersNotInSubteam.map(m => m.user?.fullName || m.fullName)
                                });
                                return null;
                            })()}
                            {membersNotInSubteam.length === 0 && !loading && (
                                <Typography sx={{ color: '#9d9d9d', fontSize: '0.875rem', padding: '20px 0' }}>
                                    No members
                                </Typography>
                            )}
                            {membersNotInSubteam.map((member, index) => {
                                const memberId = getMemberId(member);
                                const isSelected = memberId && selectedMembers.has(memberId?.toString());
                                const fullName = member.user?.fullName || member.fullName || 'Unknown';
                                const email = member.user?.email || member.email || '';
                                const profilePicture = member.user?.profilePictureUrl || member.profilePictureUrl;

                                return (
                                    <Box
                                        key={`${memberId || member._id}-${searchQuery}-${index}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '12px 0',
                                            borderBottom: '1px solid #f0f0f0'
                                        }}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            onChange={() => handleMemberToggle(memberId, false)}
                                            disabled={!memberId}
                                            sx={{
                                                color: '#e0e0e0',
                                                padding: '0',
                                                marginRight: '12px',
                                                '&.Mui-checked': {
                                                    color: '#000',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    borderRadius: '0.5rem',
                                                },
                                            }}
                                        />
                                        <Avatar
                                            src={profilePicture}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                marginRight: '12px',
                                                backgroundColor: '#e0e0e0'
                                            }}
                                        >
                                            {fullName.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    color: '#4f4f4f'
                                                }}
                                            >
                                                {fullName}
                                            </Typography>
                                            {email && (
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        color: '#9d9d9d'
                                                    }}
                                                >
                                                    {email}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Bottom Section: Don't see a member text, Add them here link, and Update Members button - Sticky at bottom */}
            <Box sx={{ marginTop: 'auto', paddingTop: 3, paddingBottom: 12, flexShrink: 0 }}>
                <Grid container spacing={2} alignItems="flex-end">
                    {/* Left Side: Text and Link */}
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                            {/* Don't see a member in the list? - Normal text */}
                            <Typography
                                sx={{
                                    fontSize: '0.875rem',
                                    color: '#4f4f4f',
                                    marginBottom: 0,
                                    lineHeight: 1.2
                                }}
                            >
                                Don't see a member in the list?
                            </Typography>
                            {/* Add them here - Blue link */}
                            <Link
                                component="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate('/team/add-members');
                                }}
                                sx={{
                                    fontSize: '0.875rem',
                                    color: '#29aef8',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    lineHeight: 1.2,
                                    marginTop: 0,
                                    padding: 0,
                                    border: 'none',
                                    background: 'none',
                                    textAlign: 'left',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Add them here
                            </Link>
                        </Box>
                    </Grid>

                    {/* Right Side: Update Members Button */}
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                onClick={selectedMembersInSubteam.size > 0 ? handleRemoveMembers : handleAddMembers}
                                disabled={(selectedMembers.size === 0 && selectedMembersInSubteam.size === 0) || loading}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    borderRadius: '1.5rem',
                                    textTransform: 'none',
                                    padding: '10px 24px',
                                    '&:hover': {
                                        backgroundColor: '#333',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#9e9e9e'
                                    }
                                }}
                            >
                                {loading
                                    ? (selectedMembersInSubteam.size > 0 ? 'Removing...' : 'Adding...')
                                    : selectedMembersInSubteam.size > 0
                                        ? `Remove ${selectedMembersInSubteam.size} member${selectedMembersInSubteam.size !== 1 ? 's' : ''}`
                                        : selectedMembers.size > 0
                                            ? `Add ${selectedMembers.size} member${selectedMembers.size !== 1 ? 's' : ''}`
                                            : 'Update members'
                                }
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* CSV Upload Modal */}
            <Modal
                open={csvModalOpen}
                onClose={handleCsvModalClose}
                width={{ xs: '90%', sm: '70%', md: '55%' }}
                height="auto"
                borderRadius={8}
            >
                <Box sx={{ padding: 0, position: 'relative' }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            padding: { xs: 2, sm: 3 },
                            paddingBottom: { xs: 1, sm: 1.5 },
                            position: 'relative'
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: '1.1rem', sm: '1.4rem' },
                                color: '#333'
                            }}
                        >
                            Upload a CSV
                        </Typography>
                        <IconButton
                            onClick={handleCsvModalClose}
                            sx={{
                                color: '#828282',
                                position: 'absolute',
                                top: { xs: 8, sm: 12 },
                                right: { xs: 8, sm: 12 },
                                padding: '8px',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            <CloseOutlinedIcon sx={{ fontSize: '1.5rem' }} />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: { xs: 2, sm: 3 }, paddingTop: { xs: 0, sm: 0 }, paddingBottom: { xs: 2, sm: 3 } }}>
                        {/* Description with Download Template Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 2, marginTop: { xs: '-0.5rem', sm: '-0.5rem' } }}>
                            <Typography
                                sx={{
                                    fontSize: '0.875rem',
                                    color: '#4f4f4f',
                                    lineHeight: 1.5,
                                    flex: 1
                                }}
                            >
                                Upload a CSV with a single column named email, or quickly get started by downloading the CSV template.
                            </Typography>

                            {/* Download CSV Template - Blue Text Link */}
                            <Box
                                onClick={downloadTemplate}
                                sx={{
                                    color: '#29AEF8',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                <FileDownloadOutlinedIcon sx={{ fontSize: '1rem' }} />
                                Download CSV Template
                            </Box>
                        </Box>

                        {/* File Upload Drag & Drop */}
                        <Box sx={{ marginBottom: '2.5rem' }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '18.125rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#ffffff',
                                    borderRadius: '1.125rem',
                                    border: dragActive ? '0.125rem dashed #29AEF8' : '0.125rem dashed #E0E0E0',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: '#29AEF8',
                                        backgroundColor: 'rgba(41, 174, 248, 0.02)'
                                    }
                                }}
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                component="label"
                            >
                                <input
                                    type="file"
                                    accept=".csv,.xlsx,.xls,.ods,.txt,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.spreadsheet,text/plain,application/octet-stream"
                                    onChange={handleFileInput}
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                />
                                <Box sx={{ textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            width: '5rem',
                                            height: '5rem',
                                            backgroundColor: '#F7F7F7',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 1.5rem'
                                        }}
                                    >
                                        <CSVFileIcon sx={{ fontSize: '2.5rem', color: '#4F4F4F' }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ mb: '0.0625rem', color: '#000000', fontSize: '1.5rem', fontWeight: 600 }}>
                                        <span style={{ color: '#29AEF8' }}>Select CSV or Excel file</span> to upload
                                    </Typography>
                                    <Typography sx={{ color: '#4F4F4F', fontSize: '1rem', fontWeight: 500 }}>
                                        or drag and drop it here
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Column Mapping Modal */}
            <Modal
                open={columnMappingModalOpen}
                onClose={handleColumnMappingCancel}
                width={{ xs: '90%', sm: '70%', md: '50%' }}
                height="auto"
                borderRadius={8}
            >
                <Box sx={{ padding: 0, position: 'relative' }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: { xs: 2, sm: 3 },
                            paddingBottom: { xs: 1, sm: 1.5 }
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: '1.1rem', sm: '1.4rem' },
                                color: '#333'
                            }}
                        >
                            Email Column Selection
                        </Typography>
                        <IconButton
                            onClick={handleColumnMappingCancel}
                            sx={{
                                color: '#828282',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            <CloseOutlinedIcon />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: { xs: 2, sm: 3 } }}>
                        <Typography
                            sx={{
                                fontSize: '0.875rem',
                                color: '#4f4f4f',
                                marginBottom: '2rem',
                                lineHeight: 1.5
                            }}
                        >
                            Select the column from your CSV that contains the Touch User emails to be added to the subteam
                        </Typography>

                        {/* Mapping Table */}
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, border: 'none', paddingBottom: '1rem' }}>
                                            Touch Field
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, border: 'none', paddingBottom: '1rem' }}>
                                            Select CSV Column
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                            <Typography sx={{ fontSize: '0.875rem', color: '#333' }}>
                                                Touch Account Email
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ border: 'none', padding: '12px 20px' }}>
                                            <FormControl sx={{ minWidth: 250 }}>
                                                <Select
                                                    value={emailColumnMapping}
                                                    onChange={(e) => setEmailColumnMapping(e.target.value)}
                                                    displayEmpty
                                                    sx={{
                                                        fontSize: '0.875rem',
                                                        borderRadius: '12px',
                                                        '& .MuiSelect-select': {
                                                            borderRadius: '12px',
                                                            padding: '8px 12px',
                                                            paddingRight: '32px !important',
                                                        },
                                                        '& fieldset': {
                                                            border: '1px solid #e0e0e0',
                                                            borderRadius: '12px',
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="" disabled>
                                                        <em>Unmapped</em>
                                                    </MenuItem>
                                                    {csvColumns.map((column) => (
                                                        <MenuItem key={column} value={column}>
                                                            {column}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginTop: '2rem' }}>
                            <Button
                                variant="outlined"
                                onClick={handleColumnMappingCancel}
                                sx={{
                                    borderColor: '#e0e0e0',
                                    color: '#333',
                                    textTransform: 'none',
                                    padding: '10px 24px',
                                    borderRadius: '1.5rem',
                                    '&:hover': {
                                        borderColor: '#b0b0b0',
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleColumnMappingContinue}
                                disabled={!emailColumnMapping}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    textTransform: 'none',
                                    padding: '10px 24px',
                                    borderRadius: '1.5rem',
                                    '&:hover': {
                                        backgroundColor: '#333',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#9e9e9e'
                                    }
                                }}
                            >
                                Continue
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Confirmation Modal */}
            <Modal
                open={confirmationModalOpen}
                onClose={handleConfirmationCancel}
                width={{ xs: '90%', sm: '60%', md: '40%' }}
                height="auto"
                borderRadius={8}
            >
                <Box sx={{ padding: 0, position: 'relative' }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: { xs: 2, sm: 3 },
                            paddingBottom: { xs: 1, sm: 1.5 }
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: '1.1rem', sm: '1.4rem' },
                                color: '#333'
                            }}
                        >
                            Confirm Import
                        </Typography>
                        <IconButton
                            onClick={handleConfirmationCancel}
                            sx={{
                                color: '#828282',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            <CloseOutlinedIcon />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: { xs: 2, sm: 3 } }}>
                        <Typography
                            sx={{
                                fontSize: '0.875rem',
                                color: '#4f4f4f',
                                marginBottom: '2rem',
                                lineHeight: 1.5
                            }}
                        >
                            You are about to add {csvData.length} member{csvData.length !== 1 ? 's' : ''} to the {subteamData?.name || 'subteam'} subteam.
                        </Typography>

                        {/* Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleConfirmationCancel}
                                disabled={isImporting}
                                sx={{
                                    borderColor: '#e0e0e0',
                                    color: '#333',
                                    textTransform: 'none',
                                    padding: '10px 24px',
                                    borderRadius: '1.5rem',
                                    '&:hover': {
                                        borderColor: '#b0b0b0',
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleImportMembers}
                                disabled={isImporting}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    textTransform: 'none',
                                    padding: '10px 24px',
                                    borderRadius: '1.5rem',
                                    '&:hover': {
                                        backgroundColor: '#333',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#9e9e9e'
                                    }
                                }}
                            >
                                {isImporting ? 'Adding...' : 'Add members'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Snackbar - Custom positioning for info messages */}
            {snackbar.severity === 'info' ? (
                <TeamNotificationSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    handleClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    duration={0} // Info snackbar doesn't auto-close
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                />
            ) : (
                <TeamNotificationSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    handleClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    duration={4000}
                />
            )}
        </Box>
    );
};

export default TeamSubteamMembersEditContainer;

